import { Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import Chip from '@/components/General/Chip';
import { Tooltip } from '@/components/General/Tooltip';
import Unicon from '@/components/General/Unicon';
import SelectInput from '@/components/General/inputs/SelectInput';
import { Mutation } from '@/utils/react-query/Mutations';
import { FOLLOW_UP_MONITORING_MODES, getMonitoringModeLabel } from '@/utils/constants/followUp';

/**
 * MonitoringModeRow — monitoring mode badge, manager-editable (spec §7.2).
 * AUTO follows org audience rules; INCLUDE/EXCLUDE are explicit overrides.
 */
const MonitoringModeRow = ({ organizationID, personID, monitoringMode = 'AUTO', canManage }) => {
    const mutation = Mutation.followUp.setMonitoringMode({
        onSuccess: () => Toast.show({ type: 'success', text2: 'Monitoring updated' }),
        onError: (error) => {
            if (!error?.expected) Toast.show({ type: 'error', text2: 'Failed to update monitoring.' });
        },
    });

    const modeIcon = monitoringMode === 'INCLUDE' ? 'add-circle-outline'
        : monitoringMode === 'EXCLUDE' ? 'remove-circle-outline'
        : 'sync-outline';
    const modeColor = monitoringMode === 'INCLUDE' ? 'success'
        : monitoringMode === 'EXCLUDE' ? 'warning'
        : 'textSecondary';

    return (
        <View style={styles.row} testID="follow-up-monitoring-row">
            <View style={styles.iconWrap}>
                <Unicon name="eye-outline" size={16} colorVariant="paths" />
            </View>
            <View style={styles.body}>
                <View style={styles.labelRow}>
                    <Text style={styles.label}>Monitoring</Text>
                    <Tooltip appearOnTouch title="Automatic follows your organization's audiences. Include always monitors this person. Exclude stops monitoring regardless of audiences.">
                        <Unicon name="help-circle-outline" size={13} colorVariant="textTertiary" />
                    </Tooltip>
                </View>
                {canManage ? (
                    <SelectInput
                        value={monitoringMode}
                        options={FOLLOW_UP_MONITORING_MODES}
                        hideClearButton
                        hideSelectedPills
                        onUpdate={(mode) => {
                            if (mode && mode !== monitoringMode) {
                                mutation.mutate({ organizationID, personID, mode });
                            }
                        }}
                    />
                ) : (
                    <View style={styles.badgeLine}>
                        <Chip icon={modeIcon} colorVariant={modeColor}>
                            {getMonitoringModeLabel(monitoringMode)}
                        </Chip>
                    </View>
                )}
            </View>
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    row: { flexDirection: 'row', alignItems: 'center', gap: 10 },
    iconWrap: {
        width: 32,
        height: 32,
        borderRadius: 8,
        backgroundColor: colors.pathsContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    body: { flex: 1, minWidth: 0 },
    labelRow: { flexDirection: 'row', alignItems: 'center', gap: 4 },
    label: { fontSize: 11, color: colors.textSecondary, fontFamily: fonts.medium },
    badgeLine: { marginTop: 4, flexDirection: 'row' },
}));

export default MonitoringModeRow;
